import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser'
import {
  RegistrationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/typescript-types'
import {
  AuthMethodOptions,
  AuthMethodEnum,
  AuthState,
  SigninResponse,
  AuthHandlers,
  SigninArgs,
} from '../types.d'
import { ApiError, sleep, Navigation } from '../helpers'

const post = async (csrfToken: string, path: string, body?: any) => {
  const response = await fetch('/auth/v2/' + path, {
    method: 'POST',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    credentials: 'include',
    body: body ? JSON.stringify(body) : '',
  })

  let data: any = {}

  try {
    data = await response.json()
  } catch (error) {
    data = {}
  }

  if (!response.ok) {
    if (data.reset) {
      // Something is very wrong, reload the page and wait it out
      Navigation.navigate(window.location.href)
      /* istanbul ignore next */
      await sleep(5000)
      /* istanbul ignore next */
      return
    }

    throw new ApiError(
      data.error ||
        'An unknown error occurred while attempted to contact TidyHQ.',
      data,
    )
  }

  return data
}

async function queryUser(
  csrfToken: string,
  clubId: string | null,
  customDomain: string | null,
  recaptcha: string,
  email: string,
): Promise<AuthState['signin']['userInfo']> {
  return (
    await post(csrfToken, 'start', {
      recaptcha,
      email,
      club: clubId,
      custom_domain: customDomain,
    })
  ).existingUser
}

async function chooseMethod(
  csrfToken: string,
  authMethod: AuthMethodEnum,
): Promise<AuthMethodOptions> {
  const result = await post(csrfToken, `method/${authMethod}/choose`)
  return {
    type: authMethod,
    ...result,
  }
}

async function anonymousSigninPasskeyChallenge(
  csrfToken: string,
  clubId: string | null,
  customDomain?: string | null,
): Promise<{ challenge: PublicKeyCredentialRequestOptionsJSON }> {
  return await post(csrfToken, `method/anonymous_passkey/challenge`, {
    club: clubId,
    custom_domain: customDomain,
  })
}

async function anonymousSigninPasskey(
  csrfToken: string,
  credential: any,
): Promise<SigninResponse> {
  return await post(csrfToken, `method/anonymous_passkey/authenticate`, {
    credential,
  })
}

async function signInUser(
  csrfToken: string,
  { authMethod, credential, termsAgreed }: SigninArgs,
): Promise<SigninResponse> {
  return await post(csrfToken, `method/${authMethod}/authenticate`, {
    credential,
    termsAgreed,
  })
}

async function signUpUser(
  csrfToken: string,
  email: string,
  firstName: string,
  lastName: string,
): Promise<SigninResponse> {
  return await post(csrfToken, 'signup', { email, firstName, lastName })
}

async function enrollPasskeyChallenge(
  csrfToken: string,
): Promise<PublicKeyCredentialCreationOptionsJSON> {
  return await post(csrfToken, `passkey/challenge`)
}

async function enrollPasskey(
  csrfToken: string,
  challenge: RegistrationResponseJSON,
): Promise<{}> {
  return await post(csrfToken, `passkey/enroll`, { challenge })
}

function signalAllAcceptedCredentials(
  userId: string,
  allAcceptedCredentialIds: string[],
) {
  if (
    typeof window.PublicKeyCredential.signalAllAcceptedCredentials ===
    'function'
  ) {
    try {
      console.log('Signaling accepted credentials to PublicKeyCredential API')
      window.PublicKeyCredential.signalAllAcceptedCredentials({
        rpId: window.location.hostname,
        userId,
        allAcceptedCredentialIds,
      })
    } catch (error) {
      console.error('Error signaling accepted credentials:', error)
    }
  }
}

function signalCurrentUserDetails(
  userId: string,
  name: string,
  displayName: string,
) {
  if (
    typeof window.PublicKeyCredential.signalCurrentUserDetails === 'function'
  ) {
    try {
      console.log('Signaling current user details to PublicKeyCredential API')
      window.PublicKeyCredential.signalCurrentUserDetails({
        rpId: window.location.hostname,
        userId,
        name,
        displayName,
      })
    } catch (error) {
      console.error('Error signaling current user details:', error)
    }
  }
}

function signalUnknownCredential(rawId: string) {
  if (
    typeof window.PublicKeyCredential.signalUnknownCredential === 'function'
  ) {
    try {
      console.log('Signaling unknown credential to PublicKeyCredential API')
      window.PublicKeyCredential.signalUnknownCredential({
        rpId: window.location.hostname,
        credentialId: rawId,
      })
    } catch (error) {
      console.error('Error signaling unknown credential:', error)
    }
  }
}

async function fetchReturnUrl(csrfToken: string): Promise<string> {
  const data = await post(csrfToken, 'return_url')
  return data.returnUrl || '/'
}

export const handlers: AuthHandlers = {
  queryUser,
  signInUser,
  signUpUser,
  enrollPasskeyChallenge,
  enrollPasskey,
  chooseMethod,
  anonymousSigninPasskeyChallenge,
  anonymousSigninPasskey,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  signalAllAcceptedCredentials,
  signalCurrentUserDetails,
  signalUnknownCredential,
  fetchReturnUrl,
}
